import { ReactNode } from 'react'
import { cn } from 'components/UI/cn'

/**
 * Bordered container for a report table (Target-table style): a rounded,
 * hairline-bordered card with an optional filter/row toolbar above the table,
 * separated by a divider. Used across all report pages so every table reads as
 * a contained card.
 *
 * `fill`: stretch the card (and its table) to consume the remaining page
 * height instead of sitting at the table's intrinsic height. Requires the
 * page shell to be a height-constrained flex column (see REPORTING_PAGE_SHELL
 * usage in BuilderPage/ReportDetailPage) and the table to scroll internally
 * (ReportTable `fill`). Used for row-level (list) tables; aggregate tables stay
 * auto-height since they're only a few rows.
 */
export function TableCard({
  toolbar,
  children,
  fill = false,
  className,
}: {
  /** Optional toolbar row above the table (filters, row-limit, etc.). */
  toolbar?: ReactNode
  children: ReactNode
  fill?: boolean
  /** Extra classes on the card root (e.g. a max-width for a metric card). */
  className?: string
}) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-line bg-surface',
        fill && 'flex min-h-0 flex-1 flex-col',
        className
      )}
    >
      {toolbar != null && (
        <div className="flex flex-wrap items-end gap-4 border-b border-line p-4 shrink-0">
          {toolbar}
        </div>
      )}
      {fill ? (
        <div className="flex min-h-0 flex-1 flex-col">{children}</div>
      ) : (
        children
      )}
    </div>
  )
}
